import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ReactMde from 'react-mde';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import SpecComment from '../../../models/SpecComment';
import useLoadingStatus from '../../../hooks/useLoadingStatus';

interface CommentInputProps {
  specId: string
  onNewComment: (comment: SpecComment) => void
  onFocus?: () => void
}

export const CommentInput = (props: CommentInputProps) => {
  const { specId, onNewComment, onFocus } = props;
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');
  const [input, setInput] = useState<string>('');
  const { isLoading, errorMessage, setErrorMessage, executeWithStatus } = useLoadingStatus();

  // submit comment
  const submit = async () => {
    await executeWithStatus(async () => {
      onNewComment(await SpecComment.create({
        specId,
        content: input,
      }));
      setInput('');
    });
  };

  return (
    <>
      <ReactMde
        value={input}
        toolbarCommands={[
          ['bold', 'italic', 'strikethrough', 'code'],
          ['unordered-list', 'ordered-list'],
        ]}
        onChange={(value: any) => setInput(value)}
        readOnly={false}
        selectedTab={activeTab}
        minEditorHeight={100}
        maxEditorHeight={200}
        initialEditorHeight={200}
        onTabChange={setActiveTab}
        generateMarkdownPreview={(markdown: any) => Promise.resolve(
          <ReactMarkdown
            plugins={[gfm]}
            source={markdown}
          />,
        )}
        childProps={{
          textArea: {
            onFocus,
            tabIndex: -1,
            style: {
              height: '125px',
              resize: 'none'
            }
          }
        }}
      />

      {!!errorMessage && (
        <Box pt={1.5}>
          <Alert severity="error" onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        </Box>
      )}

      <Box pt={1.5} textAlign="right">
        <Button
          startIcon={isLoading && <CircularProgress color="inherit" size="1em" />}
          disabled={isLoading || !input}
          variant="contained" color="primary"
          onClick={() => submit()}
        >
          Submit
        </Button>
      </Box>
    </>
  );
};

export default CommentInput;
